import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Props as CustomInputProps } from '../../../../components/CustomInput/CustomInput';
import TranslationAccordion from '../TranslationAccordion/TranslationAccordion';

interface Props {
    base: object;
    translation: object;
    handleAttributeChange: (attribute: string, value: any) => void;
}

const TranslationTable = (props: Props) => {
    const { base, translation, handleAttributeChange } = props;

    const { t } = useTranslation();

    const [selectedSection, selectSection] = useState('');

    const objectKeys = useCallback((obj: any, prefix: string = '') => {
        // return a string array containing the object keys
        const keys: string[] = [];

        Object.keys(obj).forEach((partialKey) => {
            if (obj[partialKey] instanceof Object) {
                keys.push(...(objectKeys(obj[partialKey], (prefix.length ? (prefix + '.') : '') + partialKey)));
            }
            else {
                keys.push((prefix.length ? (prefix + '.') : '') + partialKey);
            }
        });

        return keys;
    }, []);

    const filteredKeys = useMemo(() => {
        // remove language_code and language_description from the keys array,
        // as they're dynamically set by the server and don't need translation
        return objectKeys(base).filter(key => key !== 'language_code' && key !== 'language_description');
    }, [objectKeys, base]);

    const keySections = useMemo(() => {
        // return an array of sections, each section contains keys of the last level of depth
        return filteredKeys.reduce((sections: any, key) => {
            const sectionName = key.split('.').slice(0, -1).join('.');
            const depth = key.split('.').length;

            const arrayIndex = sections.findIndex((element: any) => {
                return element.name === sectionName;
            });

            if (arrayIndex === -1) {
                // if the section doesn't exist, let's create it
                sections.push({name: sectionName, depth, keys: []});
            }

            // insert the current key in the section
            const realIndex = (arrayIndex !== -1) ? arrayIndex : (sections.length - 1);
            sections[realIndex].keys.push(key);

            return sections;
        }, []);
    }, [filteredKeys]);

    const getTranslationSectionInfo = useCallback((keys: string[]) => {
        // return the number of total and empty (untranslated) keys
        let totalKeys = 0;
        let emptyKeys = 0;
        keys.forEach((key: string) => {
            const realValue = key.split('.').reduce((value: any, entry: string) => value ? value[entry] : undefined, translation);
            if (!realValue || realValue.length === 0) {
                emptyKeys++;
            }
            totalKeys++;
        });

        return [totalKeys, emptyKeys] as const;
    }, [translation]);

    const handleSectionChange = (newKey: string) => {
        // handle the selected section accordion
        selectSection((current) => {
            if (current === newKey) {
                return '';
            }
            return newKey;
        });
    };

    // base props of the origina language inputs, read only
    const baseInputPropsTemplate: Omit<CustomInputProps, 'name'> = {
        data: base,
        fullWidth: true,
        multiline: true,
        inputProps: { tabIndex: -1}
    };

    // base props of the language being translated inputs
    const baseInputPropsTranslation: Omit<CustomInputProps, 'name'> = {
        data: translation,
        fullWidth: true,
        multiline: true,
        errorIfEmpty: true,
        handleAttributeChange
    };

    return (
        <>
            {keySections.map((section: any, index: number) => {
                const { name, keys } = section;

                const [totalKeys, emptyKeys] = getTranslationSectionInfo(keys);
                const translatedKeys = (totalKeys - emptyKeys);

                const description = t('pages.language_translation.section_progress', { translatedCount: translatedKeys, count: totalKeys });

                return (
                    <TranslationAccordion
                        expanded={selectedSection === name}
                        description={description}
                        completed={emptyKeys === 0}
                        onChange={() => handleSectionChange(name)}
                        section={section}
                        baseInputPropsTemplate={baseInputPropsTemplate}
                        baseInputPropsTranslation={baseInputPropsTranslation}
                        fixedtopmargin={index === 0}
                        key={name}
                    />
                );
            })}
        </>
    );
};

export default TranslationTable;
